/**
 * SmartSlot Component
 * Visual indicator for intelligent track insertion points
 */

import { useState, useEffect } from 'react';
import { Plus, Sparkles, Settings, Check, Loader2, Play, Pause } from 'lucide-react';
import type { SmartSlot as SmartSlotType } from '../../stores/workbench';
import { useWorkbenchStore } from '../../stores/workbench';
import { SmartSlotEditor } from './SmartSlotEditor';
import { smartSlotService } from '../../api/services/smart-slots';
import { formatScore, getScoreColor } from '../../lib/smart-slot-matcher';
import { useTrackPreview } from '@/hooks';

interface SmartSlotProps {
  slot: SmartSlotType;
}

export function SmartSlot({ slot }: SmartSlotProps) {
  const { activeSlotId, setActiveSlot, removeSmartSlot, updateSmartSlot, tracks, addTrack, removeTrack } = useWorkbenchStore();
  const isActive = activeSlotId === slot.id;
  const [isEditorOpen, setIsEditorOpen] = useState(false);

  // Match fetching state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track preview hook
  const { playPreview, stopPreview, previewTrackId, isPlaying, isPending } = useTrackPreview({
    onError: (err) => console.error('Preview playback failed:', err),
  });

  // Fetch matches when criteria changes
  useEffect(() => {
    const fetchMatches = async () => {
      // Skip if no criteria, slot is filled, or no tracks
      if (!slot.criteria || Object.keys(slot.criteria).length === 0 || slot.filled || !tracks || tracks.length === 0) {
        updateSmartSlot(slot.id, { matches: [] });
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const response = await smartSlotService.findMatches(
          slot.criteria,
          tracks.map(t => t.id), // Send track IDs directly - no Spotify API needed!
          10, // Limit to top 10 matches
          40  // Minimum score threshold
        );
        updateSmartSlot(slot.id, { matches: response.matches });
      } catch (err) {
        console.error('Failed to fetch smart slot matches:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch matches');
        updateSmartSlot(slot.id, { matches: [] });
      } finally {
        setIsLoading(false);
      }
    };

    fetchMatches();
    // Use JSON stringify for deep comparison of criteria object
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(slot.criteria), slot.filled, tracks.length]);

  // Handle filling slot with selected track (Juggle functionality)
  const handleSelectMatch = (trackId: string) => {
    // Stop any active preview playback
    stopPreview();

    // Find the track in workbench
    const trackToAdd = tracks.find((t) => t.id === trackId);
    if (!trackToAdd) {
      console.error('Track not found in workbench:', trackId);
      return;
    }

    // Check if track already exists in workbench (for Juggle functionality)
    const originalIndex = tracks.findIndex(t => t.id === trackId);
    const isAlreadyInWorkbench = originalIndex >= 0 && originalIndex !== slot.position;

    // Calculate final position accounting for removal if needed
    let finalPosition = slot.position;

    // JUGGLE: Remove from original position FIRST if it was already in workbench
    // This prevents duplicates since removeTrack filters by ID
    if (isAlreadyInWorkbench) {
      // If removing a track before the slot position, adjust position down
      if (originalIndex < slot.position) {
        finalPosition = slot.position - 1;
      }
      removeTrack(trackId);
    }

    // IMPORTANT: Mark slot as filled FIRST before adding track
    // This prevents addTrack from shifting the slot's position
    updateSmartSlot(slot.id, {
      filled: true,
      suggestedTrackId: trackId,
    });

    // Add track at calculated position
    addTrack(trackToAdd, finalPosition);
  };

  // Format criteria for display
  const criteriaText = [];
  if (slot.criteria.bpm) {
    criteriaText.push(`${slot.criteria.bpm.min}-${slot.criteria.bpm.max} BPM`);
  }
  if (slot.criteria.keys && slot.criteria.keys.length > 0) {
    const keyDisplay = slot.criteria.keys.length > 3
      ? `${slot.criteria.keys.slice(0, 3).join(', ')}+${slot.criteria.keys.length - 3}`
      : slot.criteria.keys.join(', ');
    criteriaText.push(`Keys: ${keyDisplay}`);
  }
  if (slot.criteria.energy) {
    const min = Math.round(slot.criteria.energy.min * 100);
    const max = Math.round(slot.criteria.energy.max * 100);
    criteriaText.push(`Energy: ${min}-${max}%`);
  }

  return (
    <>
      <div
        className={`
          mx-4 my-2 p-3 rounded-lg border-2 border-dashed
          transition-all
          ${
            isActive
              ? 'border-emerald-500 bg-emerald-500/10'
              : slot.filled
              ? 'border-zinc-700 bg-zinc-800/50'
              : 'border-zinc-700 bg-zinc-900/50 hover:border-emerald-600 hover:bg-emerald-500/5'
          }
        `}
      >
        <div className="flex items-center gap-3">
          {/* Icon */}
          <div
            className={`
            p-2 rounded-lg
            ${isActive ? 'bg-emerald-500/20' : 'bg-zinc-800'}
          `}
          >
            {slot.filled ? (
              <Check className="w-4 h-4 text-emerald-400" />
            ) : isLoading ? (
              <Loader2 className="w-4 h-4 text-emerald-500 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4 text-emerald-500" />
            )}
          </div>

          {/* Content */}
          <div
            className="flex-1 min-w-0 cursor-pointer"
            onClick={() => setActiveSlot(isActive ? null : slot.id)}
          >
            <div className="text-sm font-medium text-zinc-300">
              {slot.filled ? 'Track Inserted' : `Smart Slot ${slot.position + 1}`}
            </div>
            {criteriaText.length > 0 && (
              <div className="text-xs text-zinc-500 truncate">
                {criteriaText.join(' • ')}
              </div>
            )}
            {!slot.filled && (
              <div className="text-xs mt-1">
                {isLoading ? (
                  <span className="text-zinc-500">Finding matches...</span>
                ) : error ? (
                  <span className="text-red-400">Error: {error}</span>
                ) : (slot.matches && slot.matches.length > 0) ? (
                  <span className="text-emerald-400">
                    {slot.matches.length} {slot.matches.length === 1 ? 'match' : 'matches'} found
                  </span>
                ) : criteriaText.length > 0 ? (
                  <span className="text-zinc-500">No matches found</span>
                ) : null}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1">
            {!slot.filled && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsEditorOpen(true);
                }}
                className="p-2 rounded-lg hover:bg-zinc-800 text-zinc-400 hover:text-emerald-400 transition-colors"
                title="Configure criteria"
              >
                <Settings className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                removeSmartSlot(slot.id);
              }}
              className="p-2 rounded-lg hover:bg-zinc-800 text-zinc-600 hover:text-red-400 transition-colors"
              title="Remove smart slot"
            >
              <Plus className="w-4 h-4 rotate-45" />
            </button>
          </div>
        </div>

        {/* Match List (when active and has matches) */}
        {isActive && !slot.filled && slot.matches && slot.matches.length > 0 && (
          <div className="mt-3 pt-3 border-t border-zinc-800 space-y-2">
            <div className="text-xs font-medium text-zinc-400 mb-2">
              Top Matches
            </div>
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {slot.matches.map((match) => {
                // Look up track data from workbench store
                const localTrack = tracks.find((t) => t.id === match.trackId);
                const trackName = localTrack?.name || match.name || 'Unknown Track';
                const trackArtist = localTrack?.artists?.map(a => a.name).join(', ') || match.artist || 'Unknown Artist';
                return (
                  <div
                    key={match.trackId}
                    className="flex items-center justify-between p-2 rounded-lg bg-zinc-800/50 hover:bg-zinc-800 transition-colors cursor-pointer"
                    title={`${trackName} by ${trackArtist}`}
                    onClick={() => handleSelectMatch(match.trackId)}
                  >
                    <div className="flex-1 min-w-0 mr-3">
                      <div className="text-sm text-zinc-200 truncate">
                        {trackName}
                      </div>
                      <div className="text-xs text-zinc-500 truncate">
                        {trackArtist}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {/* Preview playback button */}
                      {localTrack && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (previewTrackId === match.trackId && isPlaying) {
                              stopPreview();
                            } else {
                              playPreview({
                                id: match.trackId,
                                uri: localTrack.uri,
                                durationMs: localTrack.durationMs,
                              });
                            }
                          }}
                          className="p-1.5 rounded hover:bg-zinc-700 transition-colors"
                          title={previewTrackId === match.trackId && isPlaying ? 'Stop preview' : 'Preview track'}
                          disabled={isPending}
                        >
                          {previewTrackId === match.trackId && isPlaying ? (
                            <Pause className="w-3.5 h-3.5 text-emerald-400" />
                          ) : (
                            <Play className="w-3.5 h-3.5 text-zinc-400" />
                          )}
                        </button>
                      )}
                      <span className={`text-xs font-medium ${getScoreColor(match.score)}`}>
                        {match.score}
                      </span>
                      <span className="text-xs text-zinc-600">
                        {formatScore(match.score)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Editor Modal */}
      <SmartSlotEditor
        slot={slot}
        isOpen={isEditorOpen}
        onClose={() => setIsEditorOpen(false)}
      />
    </>
  );
}
